import { useEffect, useState, useRef, useCallback } from 'react';
import { LogEntry } from '@/services/monitoringService';
import { authManager } from '@/services/authManager';

interface UseLogStreamOptions {
  enabled: boolean;
  minLevel?: string;
  filter?: string;
  maxLogs?: number;
}

interface UseLogStreamResult {
  logs: LogEntry[];
  isConnected: boolean;
  error: string | null;
  clearLogs: () => void;
  disconnect: () => void;
}

export const useLogStream = (options: UseLogStreamOptions): UseLogStreamResult => {
  const { enabled, minLevel, filter, maxLogs = 1000 } = options;
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  // Batching refs to reduce re-renders
  const pendingLogsRef = useRef<LogEntry[]>([]);
  const flushTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const BATCH_INTERVAL = 150; // ms

  const flushPendingLogs = useCallback(() => {
    if (pendingLogsRef.current.length === 0) return;

    const pending = pendingLogsRef.current;
    pendingLogsRef.current = [];

    setLogs((prevLogs) => {
      const newLogs = [...prevLogs, ...pending];
      if (newLogs.length > maxLogs) {
        return newLogs.slice(-maxLogs);
      }
      return newLogs;
    });
  }, [maxLogs]);

  const clearLogs = useCallback(() => {
    pendingLogsRef.current = [];
    setLogs([]);
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  const connect = useCallback(async () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Ensure we have a fresh token before connecting
    await authManager.getAccessToken();
    const token = authManager.getCurrentToken();
    if (!token) {
      setError('No authentication token');
      return;
    }

    const params = new URLSearchParams();
    params.set('token', token);
    if (minLevel) params.set('minLevel', minLevel);
    if (filter) params.set('filter', filter);

    const url = `/api/v1/monitoring/logs/stream?${params.toString()}`;
    const eventSource = new EventSource(url);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setIsConnected(true);
      setError(null);
      reconnectAttemptsRef.current = 0;
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Handle connection message
        if (data.type === 'connected') {
          return;
        }

        // Add to pending batch instead of updating state immediately
        pendingLogsRef.current.push(data as LogEntry);

        // Schedule flush if not already scheduled
        if (!flushTimeoutRef.current) {
          flushTimeoutRef.current = setTimeout(() => {
            flushTimeoutRef.current = null;
            flushPendingLogs();
          }, BATCH_INTERVAL);
        }
      } catch (e) {
        console.error('Failed to parse log entry:', e);
      }
    };

    eventSource.onerror = () => {
      setIsConnected(false);
      eventSource.close();
      eventSourceRef.current = null;

      // Attempt reconnection with exponential backoff
      if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        reconnectAttemptsRef.current++;

        reconnectTimeoutRef.current = setTimeout(() => {
          if (enabled) {
            connect();
          }
        }, delay);
      } else {
        setError('Connection lost. Max reconnection attempts reached.');
      }
    };
  }, [minLevel, filter, maxLogs, enabled, flushPendingLogs]);

  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  // Reconnect when filter options change
  useEffect(() => {
    if (enabled && isConnected) {
      disconnect();
      connect();
    }
  }, [minLevel, filter]);

  return {
    logs,
    isConnected,
    error,
    clearLogs,
    disconnect,
  };
};
